"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface BellRingIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface BellRingIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  className?: string;
}

const BellRingIcon = forwardRef<BellRingIconHandle, BellRingIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const bellVariants: Variants = {
      normal: { rotate: 0 },
      animate: {
        rotate: [0, -15, 13, -9, 6, -3, 0],
        transition: { duration: 1.4 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    const clapperVariants: Variants = {
      normal: { x: 0 },
      animate: {
        x: [0, -3, 3, -2, 2, 0],
        transition: { duration: 1.4 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
        animate={controls}
        initial="normal"
        variants={bellVariants}
      >
        <motion.path d="M10.268 21a2 2 0 0 0 3.464 0" variants={clapperVariants} />
        <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
      </motion.svg>
    );
  },
);

BellRingIcon.displayName = "BellRingIcon";
export { BellRingIcon };
